import { useId, type SVGProps } from 'react';

/**
 * The interface icons: Phosphor Icons in the bold weight (MIT licence,
 * https://phosphoricons.com), so the icons share one stroke and optical size.
 * The exceptions are marked where they are: the QR code icon (drawn here in
 * the same stroke), the copy icon (Lucide, ISC), the GitHub mark (Octicons,
 * MIT) and the Bridge mark. Each takes `size` (18 px unless given; the Bridge
 * mark 13 px) and the
 * usual SVG props; they inherit the text colour.
 */

type IconProps = SVGProps<SVGSVGElement> & { size?: number };

function glyph(d: string, doc: string, directional = false): (props: IconProps) => React.JSX.Element {
  const Icon = ({ size = 18, ...rest }: IconProps): React.JSX.Element => (
    // a directional glyph (forward, back, out) turns round in a right-to-left language (styles/global.css)
    <svg width={size} height={size} viewBox="0 0 256 256" fill="currentColor" aria-hidden="true" focusable="false" data-flip={directional ? '' : undefined} {...rest}>
      <path d={d} />
    </svg>
  );
  Icon.displayName = doc;
  return Icon;
}

/** The Bridge mark from the brand: four interlocking links. */
const MARK_LINKS = [
  'M2.191 5.4225 L1.431 4.662 A2.285 2.285 0 0 1 4.662 1.431 L8.8125 5.5805',
  'M7.5775 2.1905 L8.3375 1.431 A2.285 2.285 0 0 1 11.5695 4.6625 L7.5775 8.6555',
  'M10.8085 7.5775 L11.5685 8.3375 A2.285 2.285 0 0 1 8.3345 11.5695 L4.3425 7.5775',
  'M5.422 10.81 L4.662 11.5685 A2.285 2.285 0 0 1 1.431 8.3355 L5.4235 4.3425',
] as const;

/**
 * The Bridge mark: four interlocking links, drawn as strokes along the middle of each link (matching the original
 * filled shapes to a fraction of a pixel). Two layers: a pale one underneath and the full colour on top. When the
 * mark's link or button is pointed at, the top layer draws itself in over the pale one — the mark never starts
 * from nothing — and the mark itself never moves; once started it always plays to the end (see lib/markDraw.ts and the
 * `[data-bridge-mark]` rules in global.css).
 */
export function BridgeMark({ size = 13, ...rest }: IconProps): React.JSX.Element {
  return (
    <svg width={size} height={size} viewBox="0 0 13 13" fill="none" stroke="currentColor" strokeWidth={1.524} strokeLinecap="butt" strokeLinejoin="round" aria-hidden="true" focusable="false" data-bridge-mark {...rest}>
      <g data-mark-base>
        {MARK_LINKS.map((d) => (
          <path key={d} d={d} />
        ))}
      </g>
      <g data-mark-top>
        {MARK_LINKS.map((d) => (
          <path key={d} pathLength={1} d={d} />
        ))}
      </g>
    </svg>
  );
}

/**
 * The app icon in miniature (apps/apple), as the site draws it: the inverse of the page, like the BRIDGE badge in the
 * header. On a light page a flat, matte graphite tile with the mark in white (the app's own icon); on a dark page a
 * light tile with a dark mark, which would otherwise melt into the page. The colours are the `--tile-*` tokens in
 * styles/global.css, one place for both themes. The app's real icon (web/tools/apple-icon) does not follow the page.
 */
export function BridgeAppIcon({ size = 28, ...rest }: IconProps): React.JSX.Element {
  const gradient = useId();
  return (
    <svg width={size} height={size} viewBox="0 0 1024 1024" aria-hidden="true" focusable="false" {...rest}>
      <defs>
        <linearGradient id={gradient} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" style={{ stopColor: 'var(--tile-top)' }} />
          <stop offset="0.5" style={{ stopColor: 'var(--tile-mid)' }} />
          <stop offset="1" style={{ stopColor: 'var(--tile-bottom)' }} />
        </linearGradient>
      </defs>
      <rect width="1024" height="1024" rx="230" fill={`url(#${gradient})`} />
      <rect x="0.5" y="0.5" width="1023" height="1023" rx="229.5" fill="none" style={{ stroke: 'var(--tile-edge)' }} />
      <g transform="translate(512 512) scale(42.6) translate(-6.5 -6.5)" fill="none" style={{ stroke: 'var(--tile-mark)' }} strokeWidth="1.524" strokeLinejoin="round">
        {MARK_LINKS.map((d) => (
          <path key={d} d={d} />
        ))}
      </g>
    </svg>
  );
}

/** A clock. */
export const ClockIcon = glyph('M128,20A108,108,0,1,0,236,128,108.12,108.12,0,0,0,128,20Zm0,192a84,84,0,1,1,84-84A84.09,84.09,0,0,1,128,212Zm68-84a12,12,0,0,1-12,12H128a12,12,0,0,1-12-12V72a12,12,0,0,1,24,0v44h44A12,12,0,0,1,196,128Z', 'ClockIcon');

/** Show. */
export const EyeIcon = glyph('M251,123.13c-.37-.81-9.13-20.26-28.48-39.61C196.63,57.67,164,44,128,44S59.37,57.67,33.51,83.52C14.16,102.87,5.4,122.32,5,123.13a12.08,12.08,0,0,0,0,9.75c.37.82,9.13,20.26,28.49,39.61C59.37,198.34,92,212,128,212s68.63-13.66,94.48-39.51c19.36-19.35,28.12-38.79,28.49-39.61A12.08,12.08,0,0,0,251,123.13Zm-46.06,33C183.47,177.27,157.59,188,128,188s-55.47-10.73-76.91-31.88A130.36,130.36,0,0,1,29.52,128,130.45,130.45,0,0,1,51.09,99.89C72.54,78.73,98.41,68,128,68s55.46,10.73,76.91,31.89A130.36,130.36,0,0,1,226.48,128,130.45,130.45,0,0,1,204.91,156.12ZM128,84a44,44,0,1,0,44,44A44.05,44.05,0,0,0,128,84Zm0,64a20,20,0,1,1,20-20A20,20,0,0,1,128,148Z', 'EyeIcon');

/** Hide. */
export const EyeOffIcon = glyph('M56.88,31.93A12,12,0,1,0,39.12,48.07l16,17.65C20.67,88.66,5.72,121.58,5,123.13a12.08,12.08,0,0,0,0,9.75c.37.82,9.13,20.26,28.49,39.61C59.37,198.34,92,212,128,212a131.34,131.34,0,0,0,51-10l20.09,22.1a12,12,0,0,0,17.76-16.14ZM128,188c-29.59,0-55.47-10.73-76.91-31.88A130.69,130.69,0,0,1,29.52,128c5.27-9.31,18.79-29.9,42-44.29l90.09,99.11A109.33,109.33,0,0,1,128,188Zm123-55.12c-.36.81-9,20-28,39.16a12,12,0,1,1-17-16.9A130.48,130.48,0,0,0,226.48,128a130.36,130.36,0,0,0-21.57-28.12C183.46,78.73,157.59,68,128,68c-3.35,0-6.7.14-10,.42a12,12,0,1,1-2-23.91c3.93-.34,8-.51,12-.51,36,0,68.63,13.67,94.49,39.52,19.35,19.35,28.11,38.8,28.48,39.61A12.08,12.08,0,0,1,251,132.88Z', 'EyeOffIcon');

/** Opens downwards. */
export const ChevronDownIcon = glyph('M216.49,104.49l-80,80a12,12,0,0,1-17,0l-80-80a12,12,0,0,1,17-17L128,159l71.51-71.52a12,12,0,0,1,17,17Z', 'ChevronDownIcon');

/** Goes on. */
export const ChevronRightIcon = glyph('M184.49,136.49l-80,80a12,12,0,0,1-17-17L159,128,87.51,56.49a12,12,0,1,1,17-17l80,80A12,12,0,0,1,184.49,136.49Z', 'ChevronRightIcon', true);

/** Attach a file: Lucide's paperclip (ISC) — a roomier clip than Phosphor's. */
export const PaperclipIcon = line(<path d="m21.44 11.05-9.19 9.19a6 6 0 0 1-8.49-8.49l8.57-8.57A4 4 0 1 1 18 8.84l-8.59 8.57a2 2 0 0 1-2.83-2.83l8.49-8.48" />, 'PaperclipIcon');

/** An alert: a circle with an exclamation mark (Lucide's “circle-alert”, ISC). */
export const AlertCircleIcon = line(
  <>
    <circle cx="12" cy="12" r="10" />
    <line x1="12" x2="12" y1="8" y2="12" />
    <line x1="12" x2="12.01" y1="16" y2="16" />
  </>,
  'AlertCircleIcon',
);

/** A link. */
export const LinkIcon = glyph('M87.5,151.52l64-64a12,12,0,0,1,17,17l-64,64a12,12,0,0,1-17-17Zm131-114a60.08,60.08,0,0,0-84.87,0L103.51,67.61a12,12,0,0,0,17,17l30.07-30.06a36,36,0,0,1,50.93,50.92L171.4,135.52a12,12,0,1,0,17,17l30.08-30.06A60.09,60.09,0,0,0,218.45,37.55ZM135.52,171.4l-30.07,30.08a36,36,0,0,1-50.92-50.93l30.06-30.07a12,12,0,0,0-17-17L37.55,133.58a60,60,0,0,0,84.88,84.87l30.06-30.07a12,12,0,0,0-17-17Z', 'LinkIcon');


/** Two devices connected. */
export const PlugIcon = glyph('M240.49,15.51a12,12,0,0,0-17,0L173.94,65.09l-2.54-2.55a36.05,36.05,0,0,0-50.91,0L100,83l-3.51-3.52a12,12,0,0,0-17,17L83,100,62.54,120.49a36,36,0,0,0,0,50.91l2.55,2.54L15.51,223.51a12,12,0,0,0,17,17l49.57-49.58,2.54,2.55a36.06,36.06,0,0,0,50.91,0L156,173l3.51,3.52a12,12,0,0,0,17-17L173,156l20.49-20.49a36,36,0,0,0,0-50.91l-2.55-2.54,49.58-49.57A12,12,0,0,0,240.49,15.51Zm-121.95,161a12,12,0,0,1-17,0L79.51,154.43a12,12,0,0,1,0-17L100,117l39,39Zm58-57.95h0L156,139l-39-39,20.49-20.49a12,12,0,0,1,17,0l22.06,22.06a12,12,0,0,1,0,17ZM85.27,33.37a12,12,0,0,1,21.46-10.74l8,16A12,12,0,1,1,93.27,49.37Zm-68,57.26a12,12,0,0,1,16.1-5.36l16,8a12,12,0,1,1-10.74,21.46l-16-8A12,12,0,0,1,17.27,90.63Zm221.46,74.74a12,12,0,0,1-16.1,5.36l-16-8a12,12,0,0,1,10.74-21.46l16,8A12,12,0,0,1,238.73,165.37Zm-68,57.26a12,12,0,1,1-21.46,10.74l-8-16a12,12,0,0,1,21.46-10.74Z', 'PlugIcon');

/** Copy: Lucide's “copy” (ISC, lucide.dev) — two rounded sheets, the light, tidy shape people know. */
export const CopyIcon = ({ size = 18, ...rest }: IconProps): React.JSX.Element => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" focusable="false" {...rest}>
    <rect width="14" height="14" x="8" y="8" rx="2" ry="2" />
    <path d="M4 16c-1.1 0-2-.9-2-2V4c0-1.1.9-2 2-2h10c1.1 0 2 .9 2 2" />
  </svg>
);

/**
 * The share tabs' icons: Lucide (ISC, lucide.dev), a light 2-unit line on a 24 grid — the same family as the copy
 * icon, calmer than the bold glyphs at the tabs' small size.
 */
function line(children: React.ReactNode, name: string): (props: IconProps) => React.JSX.Element {
  const Icon = ({ size = 16, ...rest }: IconProps): React.JSX.Element => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" focusable="false" {...rest}>
      {children}
    </svg>
  );
  Icon.displayName = name;
  return Icon;
}
export const TabLinkIcon = line(
  <>
    <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
    <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
  </>,
  'TabLinkIcon',
);
export const TabQrIcon = line(
  <>
    <rect width="5" height="5" x="3" y="3" rx="1" />
    <rect width="5" height="5" x="16" y="3" rx="1" />
    <rect width="5" height="5" x="3" y="16" rx="1" />
    <path d="M21 16h-3a2 2 0 0 0-2 2v3M21 21v.01M12 7v3a2 2 0 0 1-2 2H7M3 12h.01M12 3h.01M12 16v.01M16 12h1M21 12v.01M12 21v-1" />
  </>,
  'TabQrIcon',
);
export const TabCodeIcon = line(
  <>
    <rect width="20" height="16" x="2" y="4" rx="2" />
    <path d="M6 8h.01M10 8h.01M14 8h.01M18 8h.01M8 12h.01M12 12h.01M16 12h.01M7 16h10" />
  </>,
  'TabCodeIcon',
);
export const TabNearbyIcon = line(
  <>
    <path d="M4.222 10.222a11 11 0 0 1 15.556 0M8.111 14.111a5.5 5.5 0 0 1 7.778 0" />
    <circle cx="12" cy="17.6" r="1.65" fill="currentColor" stroke="none" />
  </>,
  'TabNearbyIcon',
);

/** Done. */
export const CheckIcon = glyph('M232.49,80.49l-128,128a12,12,0,0,1-17,0l-56-56a12,12,0,1,1,17-17L96,183,215.51,63.51a12,12,0,0,1,17,17Z', 'CheckIcon');

/** Close. */
export const CloseIcon = glyph('M208.49,191.51a12,12,0,0,1-17,17L128,145,64.49,208.49a12,12,0,0,1-17-17L111,128,47.51,64.49a12,12,0,0,1,17-17L128,111l63.51-63.52a12,12,0,0,1,17,17L145,128Z', 'CloseIcon');

/** Delete. */
export const TrashIcon = glyph('M216,48H180V36A28,28,0,0,0,152,8H104A28,28,0,0,0,76,36V48H40a12,12,0,0,0,0,24h4V208a20,20,0,0,0,20,20H192a20,20,0,0,0,20-20V72h4a12,12,0,0,0,0-24ZM100,36a4,4,0,0,1,4-4h48a4,4,0,0,1,4,4V48H100Zm88,168H68V72H188ZM116,104v64a12,12,0,0,1-24,0V104a12,12,0,0,1,24,0Zm48,0v64a12,12,0,0,1-24,0V104a12,12,0,0,1,24,0Z', 'TrashIcon');

/** Download. */
export const DownloadIcon = glyph('M228,144v64a12,12,0,0,1-12,12H40a12,12,0,0,1-12-12V144a12,12,0,0,1,24,0v52H204V144a12,12,0,0,1,24,0Zm-108.49,8.49a12,12,0,0,0,17,0l40-40a12,12,0,0,0-17-17L140,115V32a12,12,0,0,0-24,0v83L96.49,95.51a12,12,0,0,0-17,17Z', 'DownloadIcon');

/** Light. */
export const SunIcon = glyph('M116,36V20a12,12,0,0,1,24,0V36a12,12,0,0,1-24,0Zm80,92a68,68,0,1,1-68-68A68.07,68.07,0,0,1,196,128Zm-24,0a44,44,0,1,0-44,44A44.05,44.05,0,0,0,172,128ZM51.51,68.49a12,12,0,1,0,17-17l-12-12a12,12,0,0,0-17,17Zm0,119-12,12a12,12,0,0,0,17,17l12-12a12,12,0,1,0-17-17ZM196,72a12,12,0,0,0,8.49-3.51l12-12a12,12,0,0,0-17-17l-12,12A12,12,0,0,0,196,72Zm8.49,115.51a12,12,0,0,0-17,17l12,12a12,12,0,0,0,17-17ZM48,128a12,12,0,0,0-12-12H20a12,12,0,0,0,0,24H36A12,12,0,0,0,48,128Zm80,80a12,12,0,0,0-12,12v16a12,12,0,0,0,24,0V220A12,12,0,0,0,128,208Zm108-92H220a12,12,0,0,0,0,24h16a12,12,0,0,0,0-24Z', 'SunIcon');

/** Dark. */
export const MoonIcon = glyph('M236.37,139.4a12,12,0,0,0-12-3A84.07,84.07,0,0,1,119.6,31.59a12,12,0,0,0-15-15A108.86,108.86,0,0,0,49.69,55.07,108,108,0,0,0,136,228a107.09,107.09,0,0,0,64.93-21.69,108.86,108.86,0,0,0,38.44-54.94A12,12,0,0,0,236.37,139.4Zm-49.88,47.74A84,84,0,0,1,68.86,69.51,84.93,84.93,0,0,1,92.27,48.29Q92,52.13,92,56A108.12,108.12,0,0,0,200,164q3.87,0,7.71-.27A84.79,84.79,0,0,1,186.49,187.14Z', 'MoonIcon');

/** The system. */
export const MonitorIcon = glyph('M208,36H48A28,28,0,0,0,20,64V176a28,28,0,0,0,28,28H208a28,28,0,0,0,28-28V64A28,28,0,0,0,208,36Zm4,140a4,4,0,0,1-4,4H48a4,4,0,0,1-4-4V64a4,4,0,0,1,4-4H208a4,4,0,0,1,4,4Zm-40,52a12,12,0,0,1-12,12H96a12,12,0,0,1,0-24h64A12,12,0,0,1,172,228Z', 'MonitorIcon');

/** The devices the apps run on (Lucide, ISC), in the tab icons' stroke: a laptop, a phone, a tablet. */
export const LaptopIcon = line(
  <>
    <path d="M18 5a2 2 0 0 1 2 2v8.526a2 2 0 0 0 .212.897l1.068 2.127a1 1 0 0 1-.9 1.45H3.62a1 1 0 0 1-.9-1.45l1.068-2.127A2 2 0 0 0 4 15.526V7a2 2 0 0 1 2-2z" />
    <path d="M20.054 15.987H3.946" />
  </>,
  'LaptopIcon',
);
export const PhoneIcon = line(
  <>
    <rect width="14" height="20" x="5" y="2" rx="2" ry="2" />
    <path d="M12 18h.01" />
  </>,
  'PhoneIcon',
);
export const TabletIcon = line(
  <>
    <rect width="16" height="20" x="4" y="2" rx="2" ry="2" />
    <path d="M12 18h.01" />
  </>,
  'TabletIcon',
);

/** Settings: a gear (Lucide, ISC), in the tab icons' stroke. */
export const GearIcon = line(
  <>
    <path d="M12.22 2h-.44a2 2 0 0 0-2 2v.18a2 2 0 0 1-1 1.73l-.43.25a2 2 0 0 1-2 0l-.15-.08a2 2 0 0 0-2.73.73l-.22.38a2 2 0 0 0 .73 2.73l.15.1a2 2 0 0 1 1 1.72v.51a2 2 0 0 1-1 1.74l-.15.09a2 2 0 0 0-.73 2.73l.22.38a2 2 0 0 0 2.73.73l.15-.08a2 2 0 0 1 2 0l.43.25a2 2 0 0 1 1 1.73V20a2 2 0 0 0 2 2h.44a2 2 0 0 0 2-2v-.18a2 2 0 0 1 1-1.73l.43-.25a2 2 0 0 1 2 0l.15.08a2 2 0 0 0 2.73-.73l.22-.39a2 2 0 0 0-.73-2.73l-.15-.08a2 2 0 0 1-1-1.74v-.5a2 2 0 0 1 1-1.74l.15-.09a2 2 0 0 0 .73-2.73l-.22-.38a2 2 0 0 0-2.73-.73l-.15.08a2 2 0 0 1-2 0l-.43-.25a2 2 0 0 1-1-1.73V4a2 2 0 0 0-2-2z" />
    <circle cx="12" cy="12" r="3" />
  </>,
  'GearIcon',
);

/** Safe. */
export const ShieldCheckIcon = glyph('M208,36H48A20,20,0,0,0,28,56v56c0,54.29,26.32,87.22,48.4,105.29,23.71,19.39,47.44,26,48.44,26.29a12.1,12.1,0,0,0,6.32,0c1-.28,24.73-6.9,48.44-26.29,22.08-18.07,48.4-51,48.4-105.29V56A20,20,0,0,0,208,36Zm-4,76c0,35.71-13.09,64.69-38.91,86.15A126.28,126.28,0,0,1,128,219.38a126.14,126.14,0,0,1-37.09-21.23C65.09,176.69,52,147.71,52,112V60H204ZM79.51,144.49a12,12,0,1,1,17-17L112,143l47.51-47.52a12,12,0,0,1,17,17l-56,56a12,12,0,0,1-17,0Z', 'ShieldCheckIcon');

/** A file. */
export const FileIcon = glyph('M216.49,79.52l-56-56A12,12,0,0,0,152,20H56A20,20,0,0,0,36,40V216a20,20,0,0,0,20,20H200a20,20,0,0,0,20-20V88A12,12,0,0,0,216.49,79.52ZM160,57l23,23H160ZM60,212V44h76V92a12,12,0,0,0,12,12h48V212Z', 'FileIcon');

/** Out to another site. */
export const ArrowUpRightIcon = glyph('M204,64V168a12,12,0,0,1-24,0V93L72.49,200.49a12,12,0,0,1-17-17L163,76H88a12,12,0,0,1,0-24H192A12,12,0,0,1,204,64Z', 'ArrowUpRightIcon', true);

/** Forward. */
const ArrowRightGlyph = glyph('M224.49,136.49l-72,72a12,12,0,0,1-17-17L187,140H40a12,12,0,0,1,0-24H187L135.51,64.48a12,12,0,0,1,17-17l72,72A12,12,0,0,1,224.49,136.49Z', 'ArrowRightIcon', true);
/** The forward arrow carries `data-arrow`: in a button it nudges forward under the pointer (ui.module.css). */
export const ArrowRightIcon = (props: IconProps): React.JSX.Element => <ArrowRightGlyph data-arrow="" {...props} />;

/** Back. */
export const ArrowLeftIcon = glyph('M228,128a12,12,0,0,1-12,12H69l51.52,51.51a12,12,0,0,1-17,17l-72-72a12,12,0,0,1,0-17l72-72a12,12,0,0,1,17,17L69,116H216A12,12,0,0,1,228,128Z', 'ArrowLeftIcon', true);

/** Send. */
export const ArrowUpIcon = glyph('M208.49,120.49a12,12,0,0,1-17,0L140,69V216a12,12,0,0,1-24,0V69L64.49,120.49a12,12,0,0,1-17-17l72-72a12,12,0,0,1,17,0l72,72A12,12,0,0,1,208.49,120.49Z', 'ArrowUpIcon');


/** Locked. */
export const LockIcon = glyph('M208,76H180V56A52,52,0,0,0,76,56V76H48A20,20,0,0,0,28,96V208a20,20,0,0,0,20,20H208a20,20,0,0,0,20-20V96A20,20,0,0,0,208,76ZM100,56a28,28,0,0,1,56,0V76H100ZM204,204H52V100H204Z', 'LockIcon');

/** A phone. */
export const SmartphoneIcon = glyph('M176,12H80A28,28,0,0,0,52,40V216a28,28,0,0,0,28,28h96a28,28,0,0,0,28-28V40A28,28,0,0,0,176,12ZM76,76H180V180H76Zm4-40h96a4,4,0,0,1,4,4V52H76V40A4,4,0,0,1,80,36Zm96,184H80a4,4,0,0,1-4-4V204H180v12A4,4,0,0,1,176,220Z', 'SmartphoneIcon');

/** Share. */
export const ShareIcon = glyph('M220,112v96a20,20,0,0,1-20,20H56a20,20,0,0,1-20-20V112A20,20,0,0,1,56,92H76a12,12,0,0,1,0,24H60v88H196V116H180a12,12,0,0,1,0-24h20A20,20,0,0,1,220,112ZM96.49,72.49,116,53v83a12,12,0,0,0,24,0V53l19.51,19.52a12,12,0,1,0,17-17l-40-40a12,12,0,0,0-17,0l-40,40a12,12,0,1,0,17,17Z', 'ShareIcon');


/** The GitHub mark, from GitHub's own Octicons (`mark-github`, MIT): filled, in the text colour. */
export const GitHubMark = ({ size = 18, ...rest }: IconProps): React.JSX.Element => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" focusable="false" {...rest}>
    <path d="M12.5.75C6.146.75 1 5.896 1 12.25c0 5.089 3.292 9.387 7.863 10.91.575.101.79-.244.79-.546 0-.273-.014-1.178-.014-2.142-2.889.532-3.636-.704-3.866-1.35-.13-.331-.69-1.352-1.18-1.625-.402-.216-.977-.748-.014-.762.906-.014 1.553.834 1.769 1.179 1.035 1.74 2.688 1.25 3.349.948.1-.747.402-1.25.733-1.538-2.559-.287-5.232-1.279-5.232-5.678 0-1.25.445-2.285 1.178-3.09-.115-.288-.517-1.467.115-3.048 0 0 .963-.302 3.163 1.179.92-.259 1.897-.388 2.875-.388.977 0 1.955.13 2.875.388 2.2-1.495 3.162-1.179 3.162-1.179.633 1.581.23 2.76.115 3.048.733.805 1.179 1.825 1.179 3.09 0 4.413-2.688 5.39-5.247 5.678.417.36.776 1.05.776 2.128 0 1.538-.014 2.774-.014 3.162 0 .302.216.662.79.547C20.709 21.637 24 17.324 24 12.25 24 5.896 18.854.75 12.5.75Z" />
  </svg>
);

/** A globe (drawn here in the same stroke as the QR code icon): the language picker. */
export function GlobeIcon({ size = 18, ...rest }: IconProps): React.JSX.Element {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" focusable="false" {...rest}>
      <circle cx="12" cy="12" r="9" />
      <path d="M3 12h18" />
      <path d="M12 3c2.6 2.4 3.9 5.4 3.9 9s-1.3 6.6-3.9 9c-2.6-2.4-3.9-5.4-3.9-9S9.4 5.4 12 3z" />
    </svg>
  );
}
